import {FC, useState, useEffect, useCallback} from 'react'
import {getRequestLogs, getProfileLogs} from './_requests'

interface ProfileRow {
  _id: {profile_id: string; account: string}
  profile_id: string
  fb_user_id?: string
  parent_profile_id?: string
  account_id: string
  account_name: string
  account_email: string
  total_requests: number
  total_errors: number
  total_rate_limited: number
  days_count: number
  request_counts: Record<string, number>
}

interface Stats {
  total_profiles: number
  mean_requests: number
  std_dev: number
  outlier_threshold: number
  p95: number
}

const RequestLogsPage: FC = () => {
  const [profiles, setProfiles] = useState<ProfileRow[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [requestTypes, setRequestTypes] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(1)
  const [sort, setSort] = useState('total_requests')
  const [order, setOrder] = useState<'desc' | 'asc'>('desc')
  const [search, setSearch] = useState('')
  const [minRequests, setMinRequests] = useState(0)
  const [showOutliersOnly, setShowOutliersOnly] = useState(false)
  const [expandedProfile, setExpandedProfile] = useState<string | null>(null)
  const [profileDetail, setProfileDetail] = useState<any[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getRequestLogs({days, sort, order, search: search || undefined, min_requests: minRequests || undefined})
      setProfiles(res.data.profiles || [])
      setStats(res.data.stats || null)
      setRequestTypes(res.data.request_types || [])
    } catch (e) {
      console.error('Failed to load request logs:', e)
    }
    setLoading(false)
  }, [days, sort, order, search, minRequests])

  useEffect(() => { loadData() }, [loadData])

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'desc' ? 'asc' : 'desc')
    } else {
      setSort(field)
      setOrder('desc')
    }
  }

  const openProfileDetail = async (profileId: string) => {
    if (expandedProfile === profileId) {
      setExpandedProfile(null)
      return
    }
    setExpandedProfile(profileId)
    setDetailLoading(true)
    try {
      const res = await getProfileLogs(profileId, 14)
      setProfileDetail(res.data.logs || [])
    } catch { setProfileDetail([]) }
    setDetailLoading(false)
  }

  const displayed = showOutliersOnly && stats
    ? profiles.filter(p => p.total_requests > stats.outlier_threshold)
    : profiles

  const fmt = (n: number) => n.toLocaleString('he-IL')

  return (
    <div style={{padding: '24px', direction: 'rtl'}}>
      {/* Header */}
      <div style={{marginBottom: '24px'}}>
        <h2 style={{fontSize: '22px', fontWeight: 700, color: '#1e1e2d', margin: 0}}>
          <i className="bi bi-activity" style={{marginLeft: '10px', color: '#8950fc'}}></i>
          בקשות פרופילים
        </h2>
        <p style={{color: '#a1a5b7', fontSize: '13px', marginTop: '4px'}}>מעקב בקשות, זיהוי חריגים ו-rate limits</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px'}}>
          <div style={{background: 'linear-gradient(135deg, #8950fc, #7337ee)', borderRadius: '12px', padding: '16px', color: '#fff'}}>
            <div style={{fontSize: '11px', opacity: 0.8}}>פרופילים פעילים</div>
            <div style={{fontSize: '24px', fontWeight: 700}}>{fmt(stats.total_profiles)}</div>
          </div>
          <div style={{background: 'linear-gradient(135deg, #3699ff, #1b84e7)', borderRadius: '12px', padding: '16px', color: '#fff'}}>
            <div style={{fontSize: '11px', opacity: 0.8}}>ממוצע בקשות</div>
            <div style={{fontSize: '24px', fontWeight: 700}}>{fmt(stats.mean_requests)}</div>
          </div>
          <div style={{background: 'linear-gradient(135deg, #ffa800, #ee8d00)', borderRadius: '12px', padding: '16px', color: '#fff'}}>
            <div style={{fontSize: '11px', opacity: 0.8}}>סף חריגים (μ+2σ)</div>
            <div style={{fontSize: '24px', fontWeight: 700}}>{fmt(stats.outlier_threshold)}</div>
          </div>
          <div style={{background: 'linear-gradient(135deg, #f64e60, #d9364d)', borderRadius: '12px', padding: '16px', color: '#fff'}}>
            <div style={{fontSize: '11px', opacity: 0.8}}>P95</div>
            <div style={{fontSize: '24px', fontWeight: 700}}>{fmt(stats.p95)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px',
        border: '1px solid #e4e6ef', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'
      }}>
        {/* Days selector */}
        <div style={{display: 'flex', gap: '4px'}}>
          {[1, 3, 7, 14].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
              border: days === d ? '1px solid #8950fc' : '1px solid #e4e6ef',
              background: days === d ? '#f3eeff' : '#fff',
              color: days === d ? '#8950fc' : '#7e8299'
            }}>{d === 1 ? 'היום' : `${d} ימים`}</button>
          ))}
        </div>

        {/* Search */}
        <div style={{position: 'relative', flex: '1 1 200px'}}>
          <i className="bi bi-search" style={{position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#a1a5b7', fontSize: '12px'}}></i>
          <input
            type="text" placeholder="חפש פרופיל, חשבון..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{width: '100%', padding: '6px 32px 6px 10px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', direction: 'rtl'}}
          />
        </div>

        {/* Min requests */}
        <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
          <span style={{fontSize: '11px', color: '#7e8299'}}>מינימום:</span>
          <input
            type="number" min={0} value={minRequests}
            onChange={e => setMinRequests(parseInt(e.target.value) || 0)}
            style={{width: '70px', padding: '5px 8px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', textAlign: 'center'}}
          />
        </div>

        {/* Outliers toggle */}
        <button onClick={() => setShowOutliersOnly(!showOutliersOnly)} style={{
          padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
          border: showOutliersOnly ? '1px solid #f64e60' : '1px solid #e4e6ef',
          background: showOutliersOnly ? '#fff5f8' : '#fff',
          color: showOutliersOnly ? '#f64e60' : '#7e8299'
        }}>
          <i className="bi bi-exclamation-triangle" style={{marginLeft: '4px'}}></i>
          חריגים בלבד {showOutliersOnly && stats ? `(${profiles.filter(p => p.total_requests > stats.outlier_threshold).length})` : ''}
        </button>

        <span style={{fontSize: '11px', color: '#a1a5b7'}}>{displayed.length} פרופילים</span>
      </div>

      {/* Table */}
      <div style={{background: '#fff', borderRadius: '12px', border: '1px solid #e4e6ef', overflow: 'hidden'}}>
        {loading ? (
          <div style={{padding: '60px', textAlign: 'center'}}><div className="spinner-border text-primary" /></div>
        ) : displayed.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center', color: '#a1a5b7'}}>
            <i className="bi bi-inbox" style={{fontSize: '40px', display: 'block', marginBottom: '12px'}}></i>
            אין נתונים
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
              <thead>
                <tr style={{background: '#f5f8fa', borderBottom: '2px solid #e4e6ef'}}>
                  {[
                    {label: 'פרופיל', key: ''},
                    {label: 'חשבון', key: ''},
                    {label: 'בקשות', key: 'total_requests'},
                    {label: 'שגיאות', key: 'total_errors'},
                    {label: 'Rate Limited', key: 'total_rate_limited'},
                    {label: 'ימים', key: 'days_count'},
                    {label: '', key: ''},
                  ].map(h => (
                    <th key={h.label || 'actions'}
                      onClick={h.key ? () => handleSort(h.key) : undefined}
                      style={{
                        padding: '10px 12px', textAlign: 'right', fontWeight: 600,
                        color: sort === h.key ? '#8950fc' : '#5e6278', fontSize: '11px', whiteSpace: 'nowrap',
                        cursor: h.key ? 'pointer' : 'default', userSelect: 'none'
                      }}
                    >
                      {h.label}
                      {sort === h.key && <i className={`bi bi-caret-${order === 'asc' ? 'up' : 'down'}-fill`} style={{marginRight: '3px', fontSize: '9px'}} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((row, idx) => {
                  const isOutlier = stats && row.total_requests > stats.outlier_threshold
                  const isExpanded = expandedRow === row.profile_id
                  return (
                    <>
                      <tr key={row.profile_id} style={{
                        borderBottom: '1px solid #f1f1f2',
                        background: isOutlier ? '#fff5f8' : 'transparent'
                      }}
                        onMouseEnter={e => { if (!isOutlier) e.currentTarget.style.backgroundColor = '#f9fafb' }}
                        onMouseLeave={e => { if (!isOutlier) e.currentTarget.style.backgroundColor = 'transparent' }}
                      >
                        <td style={{padding: '10px 12px'}}>
                          <div style={{fontWeight: 600, fontSize: '12px', color: '#1e1e2d', fontFamily: 'monospace'}}>{row.profile_id}</div>
                          {row.fb_user_id && <div style={{fontSize: '9px', color: '#b5b5c3', fontFamily: 'monospace'}}>{row.fb_user_id}</div>}
                          {row.parent_profile_id && <div style={{fontSize: '9px', color: '#8950fc', fontFamily: 'monospace'}}>↑ {row.parent_profile_id}</div>}
                        </td>
                        <td style={{padding: '10px 12px'}}>
                          <a href={`/admin/accounts/${row.account_id}`} style={{fontWeight: 600, color: '#1e1e2d', fontSize: '12px', textDecoration: 'none'}}>{row.account_name?.trim()}</a>
                          <div style={{fontSize: '10px', color: '#a1a5b7'}}>{row.account_email}</div>
                          <div style={{fontSize: '9px', color: '#b5b5c3', fontFamily: 'monospace'}}>{row.account_id}</div>
                        </td>
                        <td style={{padding: '10px 12px'}}>
                          <span style={{
                            fontWeight: 700, fontSize: '14px',
                            color: isOutlier ? '#f64e60' : row.total_requests > (stats?.p95 || 0) ? '#ffa800' : '#1e1e2d'
                          }}>
                            {fmt(row.total_requests)}
                          </span>
                          {isOutlier && <i className="bi bi-exclamation-triangle-fill" style={{color: '#f64e60', marginRight: '4px', fontSize: '11px'}} />}
                        </td>
                        <td style={{padding: '10px 12px'}}>
                          <span style={{color: row.total_errors > 0 ? '#f64e60' : '#a1a5b7', fontWeight: row.total_errors > 0 ? 600 : 400}}>
                            {fmt(row.total_errors)}
                          </span>
                        </td>
                        <td style={{padding: '10px 12px'}}>
                          <span style={{color: row.total_rate_limited > 0 ? '#ffa800' : '#a1a5b7', fontWeight: row.total_rate_limited > 0 ? 600 : 400}}>
                            {fmt(row.total_rate_limited)}
                          </span>
                        </td>
                        <td style={{padding: '10px 12px', color: '#7e8299'}}>{row.days_count}</td>
                        <td style={{padding: '10px 12px', textAlign: 'center'}}>
                          <button onClick={() => setExpandedRow(isExpanded ? null : row.profile_id)} style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                            border: '1px solid #e4e6ef', background: isExpanded ? '#f3eeff' : '#fff',
                            color: isExpanded ? '#8950fc' : '#7e8299', cursor: 'pointer'
                          }}>
                            <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                          </button>
                          <button onClick={() => openProfileDetail(row.profile_id)} style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                            border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', marginRight: '4px'
                          }}>
                            <i className="bi bi-graph-up"></i>
                          </button>
                        </td>
                      </tr>
                      {/* Expanded: request types breakdown */}
                      {isExpanded && (
                        <tr key={row.profile_id + '-detail'}>
                          <td colSpan={7} style={{padding: '0 12px 12px', background: '#f9fafb'}}>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '12px'}}>
                              {Object.entries(row.request_counts)
                                .sort(([, a], [, b]) => b - a)
                                .map(([name, count]) => (
                                  <span key={name} style={{
                                    padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
                                    background: name.includes('Mutation') ? '#fff5f8' : '#f5f8fa',
                                    color: name.includes('Mutation') ? '#f64e60' : '#5e6278',
                                    border: `1px solid ${name.includes('Mutation') ? '#fdd' : '#e4e6ef'}`
                                  }}>
                                    <strong>{fmt(count)}</strong> {name.replace(/^Comet|Query$|Mutation$/g, '')}
                                  </span>
                                ))
                              }
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      {expandedProfile && (
        <>
          <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040}} onClick={() => setExpandedProfile(null)} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#fff', borderRadius: '16px', width: '700px', maxWidth: '95vw',
            maxHeight: '80vh', overflow: 'auto', zIndex: 1050,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', direction: 'rtl'
          }}>
            <div style={{padding: '16px 24px', borderBottom: '1px solid #e4e6ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h4 style={{margin: 0, fontSize: '16px', fontWeight: 700}}>
                <i className="bi bi-graph-up" style={{marginLeft: '8px', color: '#8950fc'}}></i>
                היסטוריה - {expandedProfile}
              </h4>
              <button onClick={() => setExpandedProfile(null)} style={{border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#a1a5b7'}}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div style={{padding: '20px'}}>
              {detailLoading ? (
                <div style={{textAlign: 'center', padding: '40px'}}><div className="spinner-border text-primary" /></div>
              ) : profileDetail.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#a1a5b7'}}>אין נתונים</div>
              ) : (
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e4e6ef'}}>
                      {['תאריך', 'בקשות', 'שגיאות', 'Rate Limited', 'סוגי בקשות'].map(h => (
                        <th key={h} style={{padding: '8px', textAlign: 'right', color: '#5e6278', fontSize: '11px'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profileDetail.map((log: any) => (
                      <tr key={log._id} style={{borderBottom: '1px solid #f1f1f2'}}>
                        <td style={{padding: '8px', fontWeight: 600}}>{new Date(log.date).toLocaleDateString('he-IL')}</td>
                        <td style={{padding: '8px', fontWeight: 700}}>{fmt(log.total_requests)}</td>
                        <td style={{padding: '8px', color: log.total_errors > 0 ? '#f64e60' : '#a1a5b7'}}>{fmt(log.total_errors || 0)}</td>
                        <td style={{padding: '8px', color: log.total_rate_limited > 0 ? '#ffa800' : '#a1a5b7'}}>{fmt(log.total_rate_limited || 0)}</td>
                        <td style={{padding: '8px'}}>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '3px'}}>
                            {Object.entries(log.request_counts || {})
                              .sort(([, a]: any, [, b]: any) => b - a)
                              .slice(0, 5)
                              .map(([name, count]: any) => (
                                <span key={name} style={{
                                  padding: '1px 5px', borderRadius: '4px', fontSize: '10px',
                                  background: name.includes('Mutation') ? '#fff5f8' : '#f5f8fa',
                                  color: name.includes('Mutation') ? '#f64e60' : '#7e8299'
                                }}>
                                  {count} {name.replace(/^Comet|Query$|Mutation$/g, '').substring(0, 25)}
                                </span>
                              ))
                            }
                            {Object.keys(log.request_counts || {}).length > 5 && (
                              <span style={{fontSize: '10px', color: '#a1a5b7'}}>+{Object.keys(log.request_counts).length - 5}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default RequestLogsPage
